/**
 * Mock endpoint handlers.
 *
 * Each public function maps 1:1 to a future Rust endpoint. The
 * `client.ts` layer is a thin switch over these for now; replace its
 * body with `fetch(...)` calls when the real backend lands.
 */

import { bytesToHex } from '@noble/hashes/utils';

import type {
  ActivityEvent,
  AttestationRecord,
  BehavioralContract,
  DomainType,
  EntityId,
  GiStatus,
  Hash256,
  Lineage,
  LineageEntry,
  Manifest,
  SignedEntity,
  VaultSnapshot,
} from '../types';

import {
  appendActivity,
  getRecord,
  hasSeededDemo,
  listRecords,
  markSeededDemo,
  nowTimestamp,
  reset,
  snapshot,
  upsertRecord,
  type GiRecord,
} from './store';
import {
  canonicalHash,
  generateKeyPair,
  randomHex,
  signDigest,
  type KeyPair,
} from './sign';

// Simulated network latency for mock fetches (ms).
const MOCK_LATENCY_MS = 180;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ─── Read-side ─────────────────────────────────────────────────────

export async function getVaultSnapshot(): Promise<VaultSnapshot> {
  await sleep(MOCK_LATENCY_MS);
  return snapshot();
}

export async function getGiDetail(root_id: EntityId): Promise<GiRecordDetail | null> {
  await sleep(MOCK_LATENCY_MS);
  const r = getRecord(root_id);
  if (!r) return null;
  return {
    manifest: r.manifest,
    signed_entity: r.signed_entity,
    contract: r.contract,
    lineage: r.lineage,
    attestations: r.attestations,
    counters: r.counters,
    status: r.statuses,
  };
}

export interface GiRecordDetail {
  manifest: Manifest;
  signed_entity: SignedEntity;
  contract: BehavioralContract;
  lineage: Lineage;
  attestations: AttestationRecord[];
  counters: GiRecord['counters'];
  status: GiStatus[];
}

// ─── Write-side (Spark + Verify stubs for later waves) ─────────────

export async function resetMock(): Promise<void> {
  reset();
  await sleep(40);
}

/**
 * Seed a small set of demo GIs so the Vault's populated layout is
 * visible without Sparking anything by hand. Idempotent — repeated
 * calls do nothing once the demo has been seeded.
 */
export async function seedDemoData(): Promise<VaultSnapshot> {
  await sleep(MOCK_LATENCY_MS);
  if (hasSeededDemo() && listRecords().length > 0) {
    return snapshot();
  }
  const kp = generateKeyPair();

  const demos: Array<{
    name: string;
    description: string;
    domain: DomainType;
    status: GiStatus[];
    counters: Partial<GiRecord['counters']>;
    minutes_ago: number;
  }> = [
    {
      name: 'my-reverb',
      description: 'Soft-plate reverb. Signed and ready for the marketplace.',
      domain: 'Audio',
      status: ['signed', 'published'],
      counters: { signatures: 3, verifications: 17, executions: 142, forks: 2 },
      minutes_ago: 60 * 6,
    },
    {
      name: 'qa-checklist',
      description: 'Food-safety QA checklist — FSSC 22000 aligned.',
      domain: 'Process',
      status: ['signed', 'connected'],
      counters: { signatures: 1, verifications: 8, executions: 34, forks: 0 },
      minutes_ago: 60 * 30,
    },
    {
      name: 'ekg-scan',
      description: 'AMAR-compliant EKG preprocessing pipeline.',
      domain: 'Medical',
      status: ['signed', 'running'],
      counters: { signatures: 2, verifications: 4, executions: 9, forks: 0 },
      minutes_ago: 20,
    },
    {
      name: 'audit-batch',
      description: 'Monthly audit pack — Amendment 13 compliant.',
      domain: 'Financial',
      status: ['signed', 'published', 'forked'],
      counters: { signatures: 5, verifications: 43, executions: 12, forks: 4 },
      minutes_ago: 60 * 48,
    },
  ];

  demos.forEach((d, i) => {
    const rec = buildDemoRecord(kp, d, i);
    upsertRecord(rec);
  });

  const activity: ActivityEvent[] = [
    {
      kind: 'verified',
      root_id: demos[0]!.name as EntityId,
      name: 'my-reverb',
      at: nowTimestamp(),
      ok: true,
    },
    {
      kind: 'revenue_in',
      root_id: demos[3]!.name as EntityId,
      name: 'audit-batch',
      at: { iso: agoIso(60 * 4), source: 'Monotonic' },
      amount_ils: 12,
    },
    {
      kind: 'forked',
      root_id: demos[2]!.name as EntityId,
      name: 'ekg-scan',
      at: { iso: agoIso(60 * 12), source: 'Monotonic' },
      by: 'anon-researcher',
    },
    {
      kind: 'sparked',
      root_id: demos[1]!.name as EntityId,
      name: 'qa-checklist',
      at: { iso: agoIso(60 * 30), source: 'Monotonic' },
    },
  ];
  activity.forEach(appendActivity);

  markSeededDemo();
  return snapshot();
}

function buildDemoRecord(
  authorKp: KeyPair,
  d: {
    name: string;
    description: string;
    domain: DomainType;
    status: GiStatus[];
    counters: Partial<GiRecord['counters']>;
    minutes_ago: number;
  },
  index: number,
): GiRecord {
  const contract: BehavioralContract = {
    inputs: [
      { name: 'in', data_type: 'Document', required: true },
    ],
    outputs: [
      { name: 'out', data_type: 'Document', required: true },
    ],
    parameters: [],
    behavior_rules: [],
    domain_adapter: d.domain,
    macros: [],
  };

  const root_id = randomUuid();
  const contract_hash = canonicalHash(contract);
  const source_hash: Hash256 = `sha256:${randomHex(32)}`;
  const wasm_hash: Hash256 = `sha256:${randomHex(32)}`;
  const created_at = { iso: agoIso(d.minutes_ago), source: 'Monotonic' as const };

  const manifest: Manifest = {
    gi_version: '0.1.0',
    root_id,
    version: index === 0 ? '1.2.0' : index === 1 ? '0.3.1' : index === 2 ? '2.0.0' : '1.0.0',
    name: d.name,
    description: d.description,
    domain: d.domain,
    author: { name: 'you', public_key: authorKp.public_key },
    contract_hash,
    source_hash,
    wasm_hash,
    compiler: { name: 'mock-compiler', version: '0.1.0', target: 'wasm32-unknown' },
    lineage: { root_id, depth: 0 },
    created_at,
  };

  const manifest_hash = canonicalHash(manifest);
  const signature = signDigest(hexToBytes(manifest_hash.slice('sha256:'.length)), authorKp);

  const signed_entity: SignedEntity = {
    root_id,
    version: manifest.version,
    manifest_hash,
    signature,
    signer_public_key: authorKp.public_key,
    created_at,
  };

  const lineage: Lineage = {
    root_id,
    entries: [
      {
        index: 0,
        root_id,
        version: manifest.version,
        action: 'Genesis',
        parent_hash: null,
        entry_hash: canonicalHash({ root_id, version: manifest.version, parent: null }),
        signer: authorKp.public_key,
        signed_at: created_at,
      } satisfies LineageEntry,
    ],
  };

  return {
    manifest,
    signed_entity,
    contract,
    lineage,
    attestations: [],
    statuses: d.status,
    counters: {
      signatures: d.counters.signatures ?? 1,
      verifications: d.counters.verifications ?? 0,
      executions: d.counters.executions ?? 0,
      forks: d.counters.forks ?? 0,
    },
    secret_key_hex: bytesToHex(authorKp.secret_key),
  };
}

// ─── Helpers ───────────────────────────────────────────────────────

function agoIso(minutesAgo: number): string {
  const d = new Date(Date.now() - minutesAgo * 60_000);
  return d.toISOString();
}

function randomUuid(): EntityId {
  // Prefer the browser's built-in when present.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  const hex = randomHex(16);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `4${hex.slice(13, 16)}`,
    `8${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-');
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}
